import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Github, Linkedin, Mail, Menu, X } from "lucide-react";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/experience", label: "Experience" },
  { to: "/projects", label: "Projects" },
  { to: "/contact", label: "Contact" }
];

const socialLinks = [
  { icon: Github, href: "https://github.com/nfatihakkin", label: "GitHub" },
  { icon: Linkedin, href: "https://linkedin.com/in/nfatihakkin", label: "LinkedIn" },
  { icon: Mail, href: "mailto:nfatihakkin@gmail.com", label: "Email" }
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        isScrolled
          ? "bg-ink/85 backdrop-blur-md border-b border-line"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link
            to="/"
            className="font-display text-xl font-bold text-fg hover:text-signal transition-colors"
          >
            NFA<span className="text-signal">.</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `font-mono text-xs tracking-wider uppercase transition-colors ${
                    isActive ? "text-signal" : "text-fg-dim hover:text-fg"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-fg-mute hover:text-signal transition-colors"
                aria-label={social.label}
              >
                <social.icon className="w-[18px] h-[18px]" />
              </a>
            ))}
          </div>

          <button
            className="md:hidden text-fg"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            aria-label="Toggle mobile menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden bg-panel border border-line rounded-lg mt-2 mb-3 py-4">
            <div className="flex flex-col gap-1 px-2">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-2.5 rounded-md font-mono text-sm uppercase tracking-wider transition-colors ${
                      isActive
                        ? "text-signal bg-signal/10"
                        : "text-fg-dim hover:text-fg hover:bg-panel2"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="flex justify-center gap-8 pt-4 mt-3 border-t border-line">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-fg-mute hover:text-signal transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
